import { createFileRoute } from "@tanstack/react-router";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { SHOWROOM_SCRIPT_ID } from "builder-website/src/constants";
import Page from "lib/components/layout/Page";
import { getBuilderShowroomScript } from "lib/components/showroom2/edit/showroomEmbedUtils";
import { getBuilderMasterPlansURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useEffect } from "react";
import styles from "./index.module.scss";

/**
 * A mock page for testing Showroom Intro functionality embedded into a builder's website.
 * - Launch from custom button
 * - Navigate back from Showroom Intro
 * - Send events from Showroom Intro to builder's Google Analytics (*if enabled)
 */

function ShowroomIntroEmbed() {
  const { builderId, planId, planType } = useAppRouterParams();

  const showroomScript = builderId ? getBuilderShowroomScript({ builderId }) : null;

  const { data: masterPlans } = useFetch<ClientMasterPlan[]>(builderId ? getBuilderMasterPlansURL({ builderId }) : "", [
    builderId,
  ]);

  const masterPlan = masterPlans?.[0];

  useEffect(() => {
    if (!builderId || !planId || !showroomScript) {
      return;
    }
    // Inject the embed script into <head> using DOM APIs instead of
    // innerHTML += (which destroys and recreates all head elements).
    // Guard against duplicate injection from StrictMode remounts.
    if (!document.getElementById(SHOWROOM_SCRIPT_ID)) {
      const container = document.createElement("div");
      container.innerHTML = showroomScript;
      const scriptTag = container.querySelector("script");
      if (scriptTag) {
        document.head.appendChild(scriptTag);
      }
      const linkTag = container.querySelector("link");
      if (linkTag) {
        document.head.appendChild(linkTag);
      }
    }
    loadScript(SHOWROOM_SCRIPT_ID);
  }, [builderId, planId, showroomScript]);

  return (
    <Page pageTitle="Embedded Showroom Intro">
      <div className={styles.widgets}>
        {/* An undocumented feature is to add data-higharc-options={optionMap} where the optionMap "deep links" to specific option choices.
        /*  eg. ?o=uCJsQqM+zT8G3eE+Vv1ArSM (optionMap='uCJsQqM+zT8G3eE+Vv1ArSM' )
        */}
        <button
          data-higharc-plan-id={planId}
          data-higharc-plan-type={planType}
          data-testid="showroom-intro-button-main"
        >
          Open Showroom Intro for Chosen Plan
        </button>
        {masterPlan && (
          <div>
            <p>Tests multiple plan buttons on a single page:</p>
            <button
              data-higharc-plan-id={masterPlan.id}
              data-higharc-plan-type="master_plans"
              data-testid="showroom-intro-button-master-plan"
            >
              Open Showroom Intro for Master Plan
            </button>
          </div>
        )}
      </div>
    </Page>
  );
}

// Converts an inert script tag (set via innerHTML) into a live script element.
// Marks the element with a data attribute to avoid re-execution on StrictMode remounts.
function loadScript(id: string) {
  const oldScriptEl = document.getElementById(id);
  if (!oldScriptEl || oldScriptEl.dataset.loaded) {
    return;
  }

  const newScriptEl = document.createElement("script");
  Array.from(oldScriptEl.attributes).forEach((attr) => {
    newScriptEl.setAttribute(attr.name, attr.value);
  });
  newScriptEl.dataset.loaded = "true";

  const scriptText = document.createTextNode(oldScriptEl.innerHTML);
  newScriptEl.appendChild(scriptText);

  oldScriptEl.parentNode?.replaceChild(newScriptEl, oldScriptEl);
}

export const Route = createFileRoute("/debug/builder-website/showroom-intro")({
  component: ShowroomIntroEmbed,
});
